import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { inr, inr2, num, MONTHS } from "@/lib/format";
import { KpiCard, PageLoader, EmptyState, CHART_COLORS } from "@/components/shared";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  ResponsiveContainer, Tooltip, PieChart, Pie, Cell, Legend,
  ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid,
} from "recharts";
import { IndianRupee, Wallet, PiggyBank, Clock, BookCopy, BarChart3, FileText, FileSpreadsheet, Trophy, CalendarDays } from "lucide-react";
import { toast } from "sonner";

const tooltipStyle = {
  background: "hsl(var(--popover))",
  border: "1px solid hsl(var(--border))",
  borderRadius: 8,
  color: "hsl(var(--popover-foreground))",
};

export default function AuthorDashboard() {
  const [platform, setPlatform] = useState("all");
  const [year, setYear] = useState("all");
  const [month, setMonth] = useState("all");
  const [book, setBook] = useState("all");

  const { data, isLoading } = useQuery({
    queryKey: ["author-overview", platform, year, month, book],
    queryFn: async () => {
      const params = {};
      if (platform !== "all") params.platform = platform;
      if (year !== "all") params.year = year;
      if (month !== "all") params.month = month;
      if (book !== "all") params.book_id = book;
      return (await api.get("/author/overview", { params })).data;
    },
  });

  if (isLoading || !data) return <PageLoader />;

  const pie = data.platform_split.filter((p) => p.profit > 0);

  const download = async (fmt) => {
    try {
      const res = await api.get(`/author/report.${fmt}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `sales_report.${fmt}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not download report");
    }
  };

  return (
    <div className="space-y-8">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.1em] text-muted-foreground">Your royalties</p>
          <p className="text-sm text-muted-foreground mt-1">Track sales, profit and payouts across every platform.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" className="gap-2" data-testid="export-csv" onClick={() => download("csv")}>
            <FileSpreadsheet className="h-4 w-4" /> Export CSV
          </Button>
          <Button variant="outline" size="sm" className="gap-2" data-testid="export-pdf" onClick={() => download("pdf")}>
            <FileText className="h-4 w-4" /> Export PDF
          </Button>
        </div>
      </div>

      {(data.best_month || data.top_platform) && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <Card data-testid="insight-best-month" className="p-5 flex items-center gap-4 bg-secondary border-secondary">
            <div className="h-11 w-11 rounded-lg bg-background/60 flex items-center justify-center shrink-0">
              <CalendarDays className="h-5 w-5 text-primary" />
            </div>
            <div className="min-w-0">
              <p className="text-xs uppercase tracking-[0.1em] text-muted-foreground">Best month</p>
              <p className="font-head font-semibold text-lg truncate">
                {data.best_month ? `${data.best_month.label}` : "—"}
                {data.best_month && <span className="text-primary"> · {inr(data.best_month.profit)}</span>}
              </p>
            </div>
          </Card>
          <Card data-testid="insight-top-platform" className="p-5 flex items-center gap-4 bg-secondary border-secondary">
            <div className="h-11 w-11 rounded-lg bg-background/60 flex items-center justify-center shrink-0">
              <Trophy className="h-5 w-5 text-primary" />
            </div>
            <div className="min-w-0">
              <p className="text-xs uppercase tracking-[0.1em] text-muted-foreground">Top platform</p>
              <p className="font-head font-semibold text-lg truncate">
                {data.top_platform ? data.top_platform.platform : "—"}
                {data.top_platform && <span className="text-primary"> · {inr(data.top_platform.profit)}</span>}
              </p>
            </div>
          </Card>
        </div>
      )}
      {/* KPIs — lifetime */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
        <KpiCard testid="kpi-available" label="Redeemable Profit" value={inr(data.available_profit)} icon={PiggyBank} accent sub="Ready to withdraw" delay={0} />
        <KpiCard testid="kpi-copies" label="Total Copies Sold" value={num(data.total_copies)} icon={BookCopy} sub="Across all platforms" delay={40} />
        <KpiCard testid="kpi-total" label="Total Royalty Profit" value={inr(data.total_profit)} icon={IndianRupee} sub={`${num(data.total_copies)} copies sold`} delay={80} />
        <KpiCard testid="kpi-redeemed" label="Already Redeemed" value={inr(data.redeemed)} icon={Wallet} sub="Approved payouts" delay={120} />
        <KpiCard testid="kpi-pending" label="Pending Requests" value={inr(data.pending_redemption)} icon={Clock} sub="Awaiting approval" delay={160} />
      </div>

      {/* Filters */}
      <Card className="p-5">
        <div className="flex flex-col sm:flex-row sm:items-end gap-4">
          <div className="flex-1">
            <p className="font-head font-medium">Sales reports</p>
            <p className="text-xs text-muted-foreground mt-0.5">Filter your sales history by platform, month or year.</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <div className="space-y-1.5">
              <p className="text-xs text-muted-foreground">Book</p>
              <Select value={book} onValueChange={setBook}>
                <SelectTrigger className="w-[170px] h-9" data-testid="filter-book"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All books</SelectItem>
                  {data.available_books?.map((b) => <SelectItem key={b.id} value={b.id}>{b.title}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <p className="text-xs text-muted-foreground">Platform</p>
              <Select value={platform} onValueChange={setPlatform}>
                <SelectTrigger className="w-[150px] h-9" data-testid="filter-platform"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All platforms</SelectItem>
                  {data.available_platforms.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <p className="text-xs text-muted-foreground">Year</p>
              <Select value={year} onValueChange={setYear}>
                <SelectTrigger className="w-[120px] h-9" data-testid="filter-year"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All years</SelectItem>
                  {data.available_years.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <p className="text-xs text-muted-foreground">Month</p>
              <Select value={month} onValueChange={setMonth}>
                <SelectTrigger className="w-[130px] h-9" data-testid="filter-month"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All months</SelectItem>
                  {MONTHS.slice(1).map((m, i) => <SelectItem key={i + 1} value={String(i + 1)}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
        <div className="mt-4 pt-4 border-t border-border flex flex-wrap gap-8">
          <div>
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Copies Sold</p>
            <p className="font-head text-2xl font-bold tabular-nums mt-1">{num(data.filtered_copies)}</p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Profit Earned</p>
            <p className="font-head text-2xl font-bold tabular-nums mt-1 text-primary">{inr(data.filtered_profit)}</p>
          </div>
        </div>
      </Card>

      {data.timeseries.length === 0 ? (
        <EmptyState icon={BarChart3} title="No sales in this view" desc="Try adjusting the filters, or check back once sales are recorded." />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Card className="p-6 lg:p-8 lg:col-span-2">
            <h3 className="font-head text-lg font-medium">Sales history</h3>
            <p className="text-xs text-muted-foreground mb-4">Copies sold (bars) and royalty profit (line) over time</p>
            <ResponsiveContainer width="100%" height={320}>
              <ComposedChart data={data.timeseries} margin={{ left: -8, right: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.5} vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
                <YAxis yAxisId="left" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
                <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
                <Tooltip
                  contentStyle={tooltipStyle}
                  formatter={(v, n) => (n === "profit" ? inr(v) : num(v))}
                  cursor={{ fill: "hsl(var(--muted))", opacity: 0.3 }}
                />
                <Legend />
                <Bar yAxisId="left" dataKey="copies" name="Copies" fill="hsl(var(--chart-1))" radius={[5, 5, 0, 0]} maxBarSize={48} />
                <Line yAxisId="right" type="monotone" dataKey="profit" name="Profit" stroke="hsl(var(--chart-2))" strokeWidth={2.5} dot={{ r: 3 }} />
              </ComposedChart>
            </ResponsiveContainer>
          </Card>

          <Card className="p-6 lg:p-8">
            <h3 className="font-head text-lg font-medium">Platform split</h3>
            <p className="text-xs text-muted-foreground mb-4">Profit share by platform</p>
            {pie.length === 0 ? (
              <p className="text-sm text-muted-foreground py-16 text-center">No profit in this view.</p>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie data={pie} dataKey="profit" nameKey="platform" cx="50%" cy="50%" outerRadius={95} paddingAngle={3}>
                    {pie.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                  </Pie>
                  <Tooltip contentStyle={tooltipStyle} formatter={(v) => inr(v)} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            )}
          </Card>
        </div>
      )}

      {/* Platform table */}
      {data.platform_split.length > 0 && (
        <Card className="overflow-hidden">
          <div className="px-6 py-4 border-b border-border">
            <h3 className="font-head font-medium">Copies & profit by platform</h3>
          </div>
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50 hover:bg-muted/50">
                <TableHead>Platform</TableHead>
                <TableHead className="text-right">Copies sold</TableHead>
                <TableHead className="text-right">Profit</TableHead>
                <TableHead className="text-right">Profit / sale</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.platform_split.map((p) => (
                <TableRow key={p.platform} data-testid={`platform-row-${p.platform}`}>
                  <TableCell className="font-medium">{p.platform}</TableCell>
                  <TableCell className="text-right tabular-nums">{num(p.copies)}</TableCell>
                  <TableCell className="text-right tabular-nums font-medium text-primary">{inr(p.profit)}</TableCell>
                  <TableCell className="text-right tabular-nums text-muted-foreground">{p.copies ? inr2(p.profit / p.copies) : "—"}</TableCell>
                </TableRow>
              ))}
              <TableRow className="bg-muted/40 hover:bg-muted/40 font-semibold border-t-2 border-border" data-testid="platform-totals-row">
                <TableCell>Total</TableCell>
                <TableCell className="text-right tabular-nums" data-testid="total-copies">{num(data.platform_split.reduce((a, p) => a + p.copies, 0))}</TableCell>
                <TableCell className="text-right tabular-nums text-primary" data-testid="total-profit">{inr(data.platform_split.reduce((a, p) => a + p.profit, 0))}</TableCell>
                <TableCell></TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Card>
      )}

      {/* Earning per copy by platform */}
      {data.platform_earnings?.length > 0 && (
        <Card className="overflow-hidden" data-testid="platform-earnings-card">
          <div className="px-6 py-4 border-b border-border">
            <h3 className="font-head font-medium">Your earning per copy sold</h3>
            <p className="text-xs text-muted-foreground mt-0.5">Profit you earn on each copy sold, by platform.</p>
          </div>
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50 hover:bg-muted/50">
                <TableHead>Platform</TableHead>
                <TableHead className="text-right">Profit per copy</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.platform_earnings.map((p) => (
                <TableRow key={p.platform} data-testid={`earning-row-${p.platform}`}>
                  <TableCell className="font-medium">{p.platform}</TableCell>
                  <TableCell className="text-right tabular-nums text-primary font-medium">
                    {p.min === p.max ? inr(p.min) : `${inr(p.min)} – ${inr(p.max)}`}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* Books */}
      {data.books.length > 0 && (
        <div>
          <h3 className="font-head text-lg font-medium mb-4 flex items-center gap-2"><BookCopy className="h-5 w-5" /> Your books</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {data.books.map((b) => (
              <Card key={b.id} data-testid={`author-book-${b.id}`} className="p-5 flex gap-4 hover:-translate-y-1 hover:shadow-md transition-transform duration-200">
                <img src={b.cover_url} alt={b.title} className="h-24 w-16 object-cover rounded-md border border-border shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="font-head font-semibold line-clamp-2 leading-tight">{b.title}</p>
                  <div className="mt-3 space-y-1">
                    <p className="text-sm"><span className="tabular-nums font-medium">{num(b.copies)}</span> <span className="text-muted-foreground">copies (this view)</span></p>
                    <p className="text-sm"><span className="tabular-nums font-medium text-primary">{inr(b.profit)}</span> <span className="text-muted-foreground">profit</span></p>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
